import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import get from 'lodash.get';
import {
  Box,
  Typography,
  AbsoluteBox,
  Stack,
  Backdrop,
  Button,
  color,
  FlexBox,
  Checkbox,
  Card,
  Grid,
  useTheme,
  useMediaQuery
} from '@kis-technologies/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { useActions } from 'hooks/useActions';
import { actions as cartActions, selectors as cartSelectors } from 'services/cart';
import { selectors as configSelectors, Configuration, LanguageField } from 'services/configuration';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { formatCurrency } from 'utils/stringFormat';
import { useStyles } from '../../styled';
import { DonationOverlayProps } from '../types';

const DonationOverlay = (props: DonationOverlayProps) => {
  const { onDeny, onConfirm } = props;
  const { t, i18n } = useTranslation();

  const { setDonationSource } = useActions({
    setDonationSource: cartActions.setDonationSource
  });

  const navigate = useNavigate();

  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const cartReserving = useSelector(cartSelectors.selectReserving);
  const cartDonation = useSelector(cartSelectors.selectDonation);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);
  const totalToCharge = useSelector(cartSelectors.selectCartTotalToCharge);

  const languageSettings: LanguageField[] = get(config, 'configuration.languageSettings', []);
  const palette = get(config, 'configuration.globalSettings.palette');
  const flatDonationAmounts: number[] = get(config, `${ticketingPath}.donations.amounts`, []);
  flatDonationAmounts.length = Math.min(flatDonationAmounts.length, 6);
  const roundupAmount = get(config, `${ticketingPath}.donations.overlay.roundup.amount`, 1);
  const roundupEnabled = get(config, `${ticketingPath}.donations.overlay.roundup.enabled`, false);
  const roundupMessage = get(
    config,
    `${ticketingPath}.donations.overlay.roundup.message.${i18n.language}`,
    t('donation.roundUpOptIn')
  );
  const overlayMessages = get(config, `${ticketingPath}.donations.overlay.messages`, {});
  const overlayHeader = get(overlayMessages, `header.${i18n.language}`, t('donation.overlayHeader'));
  const overlaySubheader = get(overlayMessages, `subheader.${i18n.language}`);
  const confirmButton = get(overlayMessages, `buttons.${i18n.language}[0]`, t('commonText.continue'));
  const cancelButton = get(overlayMessages, `buttons.${i18n.language}[1]`, t('donation.denyButton'));
  const giftAidEnabled = get(config, `${ticketingPath}.donations.giftAid.enabled`, false);
  const giftAidMessages = get(config, `${ticketingPath}.donations.giftAid.messages`, {});
  const giftAidOptIn = get(giftAidMessages, `optIn.${i18n.language}`, t('donation.giftAidOptIn'));
  const giftAidDetails = get(giftAidMessages, `details.${i18n.language}`, t('donation.giftAidDetails'));

  const classes = useStyles({})();
  const themes = useTheme();
  const matchPortrait = useMediaQuery(themes.breakpoints.down('lg'));
  const style = {
    backgroundColor: palette?.primary,
    borderColor: palette?.primary
  };

  const [selectedTile, setSelectedTile] = React.useState<number | null>(null);
  const [flatAmount, setFlatAmount] = React.useState(0);
  const [donationAmount, setDonationAmount] = React.useState(0);
  const [roundUpChecked, setRoundUpChecked] = React.useState(false);
  const [giftAidChecked, setGiftAidChecked] = React.useState(false);
  const [showGiftAidOverlay, setShowGiftAidOverlay] = React.useState(false);

  React.useEffect(() => {
    const totalToRoundUp = totalToCharge + flatAmount;
    const amountToRoundUp = roundUpChecked ? roundupAmount - (totalToRoundUp % roundupAmount) : 0;
    setDonationAmount(flatAmount + amountToRoundUp);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatAmount, roundUpChecked]);

  React.useEffect(() => {
    if (giftAidChecked) setShowGiftAidOverlay(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftAidChecked]);

  // Redirect to the next page (CustomerId) after reserving the donation
  React.useEffect(() => {
    if (donationAmount > 0 && !cartReserving && !cartDonation.error) {
      setDonationSource('DonationOverlay');
      navigate('/ticketing/cart/customer');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartReserving]);

  const selectFlatAmountTile = (idx: number, amount: number) => {
    // deselect if already selected
    if (selectedTile === idx) {
      setSelectedTile(null);
      setFlatAmount(0);
    } else {
      setSelectedTile(idx);
      setFlatAmount(amount);
    }
  };

  return (
    <>
      {cartReserving && <LoadingOverlay style={{ zIndex: '2147483648' }} />}
      {!cartReserving && (
        <>
          <Backdrop sx={{ background: 'rgba(255,255,255,0.95)' }} open>
            <Grid container className={classes.overlayContainer}>
              {/* Title */}
              <Box flexOverride="0 0 60px" margin="0 10">
                <Typography variant="h2" align="center" sx={{ lineHeight: 1, whiteSpace: 'pre-wrap' }}>
                  {overlayHeader}
                </Typography>
              </Box>

              {/* Subheader */}
              {overlaySubheader && (
                <Box flexOverride="0 0 50px" margin="0 10" padding="5 0 0">
                  <Typography align="center" fontSize={28} sx={{ lineHeight: 1, whiteSpace: 'pre-wrap' }}>
                    {overlaySubheader}
                  </Typography>
                </Box>
              )}

              {/* Container for the tiles, checkboxes, totals, error */}
              <FlexBox flexOverride="1 0 auto" direction="column" fullWidth padding="5 10 7">
                {/* Tiles for flat amounts */}
                {flatDonationAmounts.length > 0 ? (
                  <FlexBox
                    flexOverride="1 0 auto"
                    justify="center"
                    margin={flatDonationAmounts.length === 4 ? '0 16' : '0'}
                    sx={{ gap: '24px' }}
                  >
                    {flatDonationAmounts.map((amount, idx) => {
                      const isSelected = typeof selectedTile === 'number' && selectedTile === idx;

                      return (
                        <Card
                          fullHeight
                          hasBorder={isSelected}
                          borderColor={palette?.primary}
                          key={idx}
                          sx={{ boxShadow: 'none' }}
                        >
                          <Card.ActionArea onClick={() => selectFlatAmountTile(idx, amount)}>
                            <FlexBox
                              border={`solid 1px ${color.LIGHT_GRAY}`}
                              padding="2 4"
                              align="center"
                              justify="between"
                              sx={{
                                minHeight: '80px',
                                width: matchPortrait ? '245px' : '295px'
                              }}
                            >
                              <Checkbox
                                size="medium"
                                label={
                                  <Typography fontSize={32} fontWeight={600}>
                                    {formatCurrency(amount, {}, languageSettings)}
                                  </Typography>
                                }
                                checked={isSelected}
                                className={classes.donationTileCheckbox}
                              />
                            </FlexBox>
                          </Card.ActionArea>
                        </Card>
                      );
                    })}
                  </FlexBox>
                ) : null}

                {/* Round up checkbox */}
                {roundupEnabled && (
                  <FlexBox padding="5 0 0 0">
                    <Checkbox
                      size="large"
                      data-testid="donation-checkbox"
                      label={
                        <Grid>
                          <Typography fontSize="32px" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                            {roundupMessage}
                          </Typography>
                        </Grid>
                      }
                      checked={roundUpChecked}
                      onChange={() => setRoundUpChecked(!roundUpChecked)}
                    />
                  </FlexBox>
                )}

                {/* Gift Aid checkbox */}
                {giftAidEnabled && (
                  <FlexBox padding="5 0 0 0">
                    <Checkbox
                      size="large"
                      data-testid="gift-aid-checkbox"
                      label={
                        <Grid>
                          <Typography fontSize="32px" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                            {giftAidOptIn}
                          </Typography>
                        </Grid>
                      }
                      checked={giftAidChecked}
                      onChange={() => setGiftAidChecked(!giftAidChecked)}
                    />
                  </FlexBox>
                )}

                {/* Donation & Total labels/amounts */}
                <FlexBox flexOverride="none" direction="row" justify="center" align="center" padding="5 0">
                  <FlexBox direction="column" align="end" margin="0 2 0 0">
                    <Typography fontSize={30} align="right">
                      {`${t('donation.label')}${t('commonText.colon')} `}
                    </Typography>
                    <Typography fontSize={30} fontWeight={600} align="right">
                      {`${t('commonText.total')}${t('commonText.colon')} `}
                    </Typography>
                  </FlexBox>

                  <FlexBox direction="column" align="end" margin="0 0 0 2">
                    <Typography fontSize={30} align="right">
                      {formatCurrency(donationAmount, {}, languageSettings)}
                    </Typography>
                    <Typography fontSize={30} fontWeight={600} align="right">
                      {formatCurrency(totalToCharge + donationAmount, {}, languageSettings)}
                    </Typography>
                  </FlexBox>
                </FlexBox>

                {/* Error */}
                <FlexBox fullWidth justify="center">
                  <Typography fontSize="28px" color="red">
                    {cartDonation.error}
                  </Typography>
                </FlexBox>
              </FlexBox>

              {/* Cancel and Confirm Buttons */}
              <AbsoluteBox bottom="-45px">
                <Stack spacing={12}>
                  <Button
                    data-testid="donation-deny"
                    customSize="x-large"
                    customColor={color.BLACK}
                    onClick={onDeny}
                    appearance="white"
                    className={classes.cancelButton}
                  >
                    {cancelButton}
                  </Button>

                  <Button
                    data-testid="donation-confirm"
                    customSize="x-large"
                    sx={style}
                    onClick={() => onConfirm(parseFloat(donationAmount.toFixed(2)))}
                    appearance="primary"
                    className={classes.confirmButton}
                    disabled={donationAmount === 0}
                  >
                    {confirmButton}
                  </Button>
                </Stack>
              </AbsoluteBox>
            </Grid>
          </Backdrop>

          <>
            {/* Gift Aid details overlay */}
            {showGiftAidOverlay && (
              <ActionOverlay
                title={''}
                description={
                  <Typography fontSize={32} fontWeight={600} align="center">
                    {giftAidDetails}
                  </Typography>
                }
                confirmText={t('commonText.confirm')}
                onConfirm={() => setShowGiftAidOverlay(false)}
                style={{ zIndex: '2147483648' }}
              />
            )}
          </>
        </>
      )}
    </>
  );
};

export default DonationOverlay;
